// src\components\account\widgets\GlobalWidgetEditor.tsx
'use client';
import { Plus, } from 'lucide-react';
import {
    Control,
    FieldErrors,
    UseFormRegister,
    UseFormSetValue,
    Controller,
    useFieldArray,
} from 'react-hook-form';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import FormSection from '../FormSection';
import TextInput from '../input/TextInput';
import TextareaInput from '../input/TextareaInput';
import { TextWidget } from './widgetItem/TextWidget';
import { ImageWidget } from './widgetItem/ImageWidget';
import { SliderWidget } from './widgetItem/SliderWidget';
import { EditorWidget } from './widgetItem/EditorWidget';
import { GalleryWidget } from './widgetItem/GalleryWidget';
import { ProductListWidget } from './widgetItem/ProductListWidget';
import { GridItemsWidget } from './widgetItem/GridItemsWidget';
import { ThirdgridWidget } from './widgetItem/ThirdGridIWidget';
import { BannerWidget } from './widgetItem/Banner';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { VideoBannerWidget } from '@/themes/tolica/components/VideoBannerWidget';
import { FullSliderWidget } from './widgetItem/FullSliderWidget';
import { StickyStoryWidget } from './widgetItem/StickyStoryWidget';
import { ScrollGalleryWidget } from './widgetItem/ScrollGalleryWidget';
import { SpecGridWidget } from './widgetItem/SpecGridWidget';
import { NewsGridWidget } from './widgetItem/NewsGridWidget';
import { ScrollShowcaseWidget } from './widgetItem/ScrollShowcaseWidget';
import { ProductSceneWidget, emptyProductSceneItem } from './widgetItem/ProductSceneWidget';




// ========== TYPES ==========
export type WidgetType = 'text' | 'image' | 'slider' | 'gallery' | 'editor' | 'product_list' | 'grid_items' | 'third_grid' | 'third_grid_line' | 'video_banner' | 'banner_1' | 'full_slider' | 'sticky_story' | 'showcase_slider' | 'spec_grid' | 'news_grid' | 'scroll_showcase' | 'product_scene'

export type WidgetWidth = '1/1' | '1/2' | '1/3' | '1/4' | '2/3' | '3/4';

export type WidgetOrientation = 'vertical' | 'horizontal';

export type WidgetDirection = 'left' | 'right' | 'center';

// ========== TYPES and COMPONENT ==========

export interface GlobalWidgetItem {
    id: string;
    type: WidgetType;
    width: WidgetWidth;
    orientation?: WidgetOrientation;
    direction?: WidgetDirection;
    image?: any;
    title?: string;
    title_en?: string;
    title_ar?: string;
    description?: string;
    description_en?: string;
    description_ar?: string;
}

interface GlobalWidgetEditorProps {
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    setValue: UseFormSetValue<any>;
    isPending?: boolean;
}

interface LanguageTabsProps {
    children: (lang: 'fa' | 'en' | 'ar') => React.ReactNode;
}

export const LanguageTabs = ({ children }: LanguageTabsProps) => {
    const { dictionary } = useDictionary();

    return (
        <Tabs defaultValue="fa" className="w-full">
            <TabsList className="border border-muted bg-primary-0">
                <TabsTrigger value="fa" className="data-[state=active]:bg-muted">
                    {dictionary.ui.language.fa}
                </TabsTrigger>
                <TabsTrigger value="en" className="data-[state=active]:bg-muted">
                    {dictionary.ui.language.en}
                </TabsTrigger>
                <TabsTrigger value="ar" className="data-[state=active]:bg-muted">
                    {dictionary.ui.language.ar}
                </TabsTrigger>
            </TabsList>
            <TabsContent value="fa">{children('fa')}</TabsContent>
            <TabsContent value="en">{children('en')}</TabsContent>
            <TabsContent value="ar">{children('ar')}</TabsContent>
        </Tabs>
    );
};

interface MultilingualInputProps {
    baseName: string;
    label: string;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const MultilingualInput = ({
    baseName,
    label,
    register,
    errors,
    disabled,
}: MultilingualInputProps) => {
    const { dictionary } = useDictionary();

    const getFieldName = (lang: string) =>
        lang === 'fa' ? baseName : `${baseName}_${lang}`;

    const getLabel = (lang: string) => {
        const langLabel =
            dictionary.ui.language[lang as keyof typeof dictionary.ui.language];
        return `${label} ${langLabel}`;
    };

    return (
        <LanguageTabs>
            {(lang) => (
                <TextInput
                    label={getLabel(lang)}
                    name={getFieldName(lang)}
                    placeholder={getLabel(lang)}
                    register={register}
                    errors={errors}
                    disabled={disabled}
                />
            )}
        </LanguageTabs>
    );
};

interface MultilingualTextareaProps {
    baseName: string;
    label: string;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const MultilingualTextareaInput = ({
    baseName,
    label,
    register,
    errors,
    disabled,
}: MultilingualTextareaProps) => {
    const { dictionary } = useDictionary();

    const getFieldName = (lang: string) =>
        lang === 'fa' ? baseName : `${baseName}_${lang}`;

    const getLabel = (lang: string) => {
        const langLabel =
            dictionary.ui.language[lang as keyof typeof dictionary.ui.language];
        return `${label} ${langLabel}`;
    };

    return (
        <LanguageTabs>
            {(lang) => (
                <TextareaInput
                    label={getLabel(lang)}
                    name={getFieldName(lang)}
                    placeholder={getLabel(lang)}
                    register={register}
                    errors={errors}
                    disabled={disabled}
                />
            )}
        </LanguageTabs>
    );
};


interface WidgetItemProps {
    widget: GlobalWidgetItem;
    index: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    setValue: UseFormSetValue<any>;
    disabled?: boolean;
    onRemove: () => void;
    move: (from: number, to: number) => void;
    fieldsLength: number;
}

const WidgetItem = ({
    widget,
    index,
    name,
    control,
    register,
    errors,
    setValue,
    disabled,
    onRemove,
    move,
    fieldsLength,
}: WidgetItemProps) => {
    const { dictionary } = useDictionary();
    const [isOpen, setIsOpen] = useState(false);

    const getWidgetTypeLabel = (type: WidgetType): string => {
        const labels: Record<WidgetType, string> = {
            text: dictionary.forms?.text,
            image: dictionary.forms?.image,
            video_banner: "بنر ویدیویی",
            slider: dictionary.forms?.slider,
            gallery: dictionary.forms?.gallery,
            editor: dictionary.forms?.content,
            product_list: "لیست محصولات",
            grid_items: "پازل چندتایی",
            third_grid: "پازل سه تایی",
            third_grid_line: "پازل سه تایی خطی",
            banner_1: "بنر",
            full_slider: "اسلایدر تمام صفحه",
            sticky_story: "روایت اسکرولی",
            showcase_slider: "گالری اسکرولی",
            spec_grid: "مشخصات فنی",
            news_grid: "کارت‌های خبر و مقاله",
            scroll_showcase: "ویترین اسکرولی",
            product_scene: "ترکیب‌های دکوراسیون",
        };
        // ویجت‌های حذف‌شده (مثل marquee) ممکنه هنوز توی صفحه‌های قدیمی ذخیره باشن
        return labels[type] || type;
    };

    const renderWidgetContent = () => {
        switch (widget.type) {
            case 'text':
                return (
                    <TextWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'image':
                return (
                    <ImageWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'video_banner':
                return (
                    <VideoBannerWidget
                        widgetIndex={index}
                        name={name}
                        register={register}
                        control={control}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'slider':
                return (
                    <SliderWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'gallery':
                return (
                    <GalleryWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'editor':
                return (
                    <EditorWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'product_list':
                return (
                    <ProductListWidget
                        widgetIndex={index}
                        name={name}
                        register={register}
                        control={control}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'grid_items':
                return (
                    <GridItemsWidget
                        widgetIndex={index}
                        name={name}
                        register={register}
                        control={control}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'third_grid':
                return (
                    <ThirdgridWidget
                        widgetIndex={index}
                        name={name}
                        register={register}
                        control={control}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'third_grid_line':
                return (
                    <ThirdgridWidget
                        widgetIndex={index}
                        name={name}
                        register={register}
                        control={control}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'banner_1':
                return (
                    <BannerWidget
                        widgetIndex={index}
                        name={name}
                        register={register}
                        control={control}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'full_slider':
                return (
                    <FullSliderWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'sticky_story':
                return (
                    <StickyStoryWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'showcase_slider':
                return (
                    <ScrollGalleryWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'spec_grid':
                return (
                    <SpecGridWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'news_grid':
                return (
                    <NewsGridWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'product_scene':
                return (
                    <ProductSceneWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
            case 'scroll_showcase':
                return (
                    <ScrollShowcaseWidget
                        widgetIndex={index}
                        name={name}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                );
        }
    };

    const hasOrientation = ['image', 'slider'].includes(widget.type);
    const hasWidth = ['image', 'slider', "text"].includes(widget.type);
    const hasDirection = ['text', 'image', 'spec_grid'].includes(widget.type);

    const hasControls = hasWidth || hasOrientation || hasDirection;


    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <FormSection
                title={
                    <CollapsibleTrigger className="flex items-center gap-2 w-full">
                        <div className="h-10 w-10 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? '' : 'rotate-90'}`} />
                        </div>
                        {`${dictionary.ui?.form?.globalWidgetItem || 'Widget'} ${index + 1} (${getWidgetTypeLabel(widget.type)})`}
                    </CollapsibleTrigger>
                }
                index={index}
                fieldsLength={fieldsLength}
                move={move}
                setValue={setValue}
                onRemove={onRemove}
                className="space-y-0"
            >
                <CollapsibleContent className="col-span-2">
                    {hasControls && <div className="col-span-2">
                        <div className="flex items-center gap-4 w-full">
                            {hasWidth && <div className="flex flex-col flex-1">
                                <label className="text-xs font-medium text-muted-foreground mb-1">
                                    {dictionary.forms?.width || 'Width'}
                                </label>
                                <Controller
                                    control={control}
                                    name={`${name}.${index}.width`}
                                    render={({ field }) => (
                                        <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="1/1">1/1</SelectItem>
                                                <SelectItem value="1/2">1/2</SelectItem>
                                                <SelectItem value="1/3">1/3</SelectItem>
                                                <SelectItem value="1/4">1/4</SelectItem>
                                                <SelectItem value="2/3">2/3</SelectItem>
                                                <SelectItem value="3/4">3/4</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>}

                            {hasOrientation && (
                                <div className="flex flex-col flex-1">
                                    <label className="text-xs font-medium text-muted-foreground mb-1">
                                        {dictionary.forms?.orientation || 'Orientation'}
                                    </label>
                                    <Controller
                                        control={control}
                                        name={`${name}.${index}.orientation`}
                                        render={({ field }) => (
                                            <Select value={field.value || 'horizontal'} onValueChange={field.onChange} disabled={disabled}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="horizontal">{dictionary.forms?.horizontal || 'Horizontal'}</SelectItem>
                                                    <SelectItem value="vertical">{dictionary.forms?.vertical || 'Vertical'}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            )}
                            {hasDirection && (
                                <div className="flex flex-col flex-1">
                                    <label className="text-xs font-medium text-muted-foreground mb-1">
                                        {dictionary.forms?.direction || 'Direction'}
                                    </label>
                                    <Controller
                                        control={control}
                                        name={`${name}.${index}.direction`}
                                        render={({ field }) => (
                                            <Select value={field.value || 'left'} onValueChange={field.onChange} disabled={disabled}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="left">{dictionary.forms?.left || 'Left'}</SelectItem>
                                                    <SelectItem value="right">{dictionary.forms?.right || 'Right'}</SelectItem>
                                                    <SelectItem value="center">{dictionary.forms?.center || 'Center'}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    </div>}

                    <div className="p-4 col-span-2 gap-4 rounded-lg grid grid-cols-1 md:grid-cols-2 rounded-b-md bg-background">
                        {renderWidgetContent()}
                    </div>
                </CollapsibleContent>

            </FormSection>
        </Collapsible>

    );
};

// ========== MAIN COMPONENT ==========
export default function GlobalWidgetEditor({
    name,
    control,
    register,
    errors,
    setValue,
    isPending,
}: GlobalWidgetEditorProps) {
    const { dictionary } = useDictionary();

    const { fields: widgetFields, append: appendWidget, remove, move } = useFieldArray({
        control,
        name,
    });

    const addWidget = (type: WidgetType) => {
        const baseItem: GlobalWidgetItem = {
            id: crypto.randomUUID(),
            type,
            width: '1/1',
            ...(type !== 'text' && { orientation: 'horizontal' }),
            ...((type === 'text' || type === 'image') && { direction: 'left' }),
            ...(type === 'spec_grid' && { direction: 'right' }),
        };

        const newItem = {
            ...baseItem,
            ...(type === 'text' && {
                items: [
                    { title: '', title_en: '', title_ar: '', description: '', description_en: '', description_ar: '' },
                ],
            }),
            ...(type === 'image' && {
                title: '',
                title_en: '',
                title_ar: '',
                description: '',
                description_en: '',
                description_ar: '',
                link: '',
                image: undefined,
            }),
            ...(type === 'video_banner' && {
                title: '',
                title_en: '',
                title_ar: '',
                buttonContent: '',
                buttonContent_en: '',
                buttonContent_ar: '',
                link: '',
                image: undefined,
            }),
            ...(type === 'slider' && {
                items: [
                    {
                        image: undefined,
                        title: '',
                        title_en: '',
                        title_ar: '',
                        description: '',
                        description_en: '',
                        description_ar: '',
                    },
                ],
            }),
            ...(type === 'full_slider' && {
                items: [
                    {
                        image: undefined,
                        image_mobile: undefined,
                        title: '', title_en: '', title_ar: '',
                        description: '', description_en: '', description_ar: '',
                        buttonContent: '', buttonContent_en: '', buttonContent_ar: '',
                        link: '',
                    },
                ],
            }),
            ...(type === 'gallery' && {
                items: [
                    {
                        type: 'text',
                        title: '',
                        title_en: '',
                        title_ar: '',
                        description: '',
                        description_en: '',
                        description_ar: '',
                    },
                ],
            }),
            ...(type === 'editor' && {
                content: '',
                content_en: '',
                content_ar: '',
            }),
            ...(type === 'product_list' && {
                type_list: 'new',
            }),
            ...(type === 'sticky_story' && {
                title: '',
                title_en: '',
                title_ar: '',
                image: undefined,
                items: [
                    { title: '', subtitle: '', description: '' },
                ],
            }),
            ...(type === 'showcase_slider' && {
                title: '',
                title_en: '',
                title_ar: '',
                description: '',
                description_en: '',
                description_ar: '',
                items: [
                    { image: undefined, title: '', title_en: '', title_ar: '', description: '', description_en: '', description_ar: '', link: '' },
                    { image: undefined, title: '', title_en: '', title_ar: '', description: '', description_en: '', description_ar: '', link: '' },
                ],
            }),
            ...(type === 'spec_grid' && {
                title: '',
                title_en: '',
                title_ar: '',
                description: '',
                description_en: '',
                description_ar: '',
                items: [
                    { image: undefined, title: '', title_en: '', title_ar: '' },
                    { image: undefined, title: '', title_en: '', title_ar: '' },
                    { image: undefined, title: '', title_en: '', title_ar: '' },
                ],
            }),
            ...(type === 'news_grid' && {
                title: '',
                title_en: '',
                title_ar: '',
                description: '',
                description_en: '',
                description_ar: '',
                items: [
                    {
                        image: undefined,
                        tag: '', tag_en: '', tag_ar: '',
                        title: '', title_en: '', title_ar: '',
                        date: '',
                        readTime: '',
                        link: '',
                    },
                    {
                        image: undefined,
                        tag: '', tag_en: '', tag_ar: '',
                        title: '', title_en: '', title_ar: '',
                        date: '',
                        readTime: '',
                        link: '',
                    },
                    {
                        image: undefined,
                        tag: '', tag_en: '', tag_ar: '',
                        title: '', title_en: '', title_ar: '',
                        date: '',
                        readTime: '',
                        link: '',
                    },
                ],
            }),
            ...(type === 'product_scene' && {
                title: '',
                title_en: '',
                title_ar: '',
                description: '',
                description_en: '',
                description_ar: '',
                items: [emptyProductSceneItem(), emptyProductSceneItem()],
            }),
            ...(type === 'scroll_showcase' && {
                items: [
                    {
                        label: '', label_en: '', label_ar: '',
                        title: '', title_en: '', title_ar: '',
                        description: '', description_en: '', description_ar: '',
                        buttonText: '', buttonText_en: '', buttonText_ar: '',
                        buttonLink: '',
                        imagePosition: 'right',
                        imageLayout: 'big_top',
                        bigImage: undefined,
                        smallImage1: undefined,
                        smallImage2: undefined,
                    },
                    {
                        label: '', label_en: '', label_ar: '',
                        title: '', title_en: '', title_ar: '',
                        description: '', description_en: '', description_ar: '',
                        buttonText: '', buttonText_en: '', buttonText_ar: '',
                        buttonLink: '',
                        imagePosition: 'right',
                        imageLayout: 'big_top',
                        bigImage: undefined,
                        smallImage1: undefined,
                        smallImage2: undefined,
                    },
                ],
            }),

        };
        appendWidget(newItem);
    };

    return (
        <div className="space-y-4">
            {widgetFields.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <p className="text-muted-foreground mb-4">
                        {dictionary.ui?.form?.noWidgets}
                    </p>
                </div>
            ) : (
                widgetFields.map((field, index) => {
                    const widget = field as unknown as GlobalWidgetItem;
                    return (
                        <WidgetItem
                            key={field.id}
                            widget={widget}
                            index={index}
                            name={name}
                            control={control}
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            disabled={isPending}
                            onRemove={() => remove(index)}
                            move={move}
                            fieldsLength={widgetFields.length}
                        />
                    );
                })
            )}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" disabled={isPending}>
                        <Plus className="h-4 w-4 mr-2" />
                        {dictionary.ui?.form?.addWidget}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => addWidget('text')}>
                        {dictionary.ui?.form?.addTextWidget}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('image')}>
                        {dictionary.ui?.form?.addImageWidget}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('video_banner')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " بنر ویدیویی"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('slider')}>
                        {dictionary.ui?.form?.addSliderWidget}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('gallery')}>
                        {dictionary.ui?.form?.addGalleryWidget}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('editor')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " " + dictionary.forms.content}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('product_list')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " لیست محصولات"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('grid_items')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " پازل چندتایی"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('third_grid')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " پازل سه تایی"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('third_grid_line')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " پازل سه تایی خطی"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('banner_1')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " بنر"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('full_slider')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " اسلایدر تمام صفحه"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('sticky_story')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " روایت اسکرولی"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('showcase_slider')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " گالری اسکرولی"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('spec_grid')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " مشخصات فنی"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('news_grid')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " کارت‌های خبر و مقاله"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('scroll_showcase')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " ویترین اسکرولی"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addWidget('product_scene')}>
                        {dictionary.common.add + " " + dictionary.nav.widget + " ترکیب‌های دکوراسیون"}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}